import { Ionicons } from '@expo/vector-icons';
import React, { useState } from 'react';
import { View } from 'react-native';

import { ThemedText } from '@/components/themed-text';
import { PressableScale } from '@/components/ui/pressable-scale';
import { usePatchDraft } from '@/hooks/use-onboarding-interview';
import { ChoiceWidgetData, WidgetProps } from './widget-types';
import { WidgetCard } from './widget-card';

const PRIMARY = '#FD5001';

type ChoiceOption = ChoiceWidgetData['options'][number];

/**
 * A pergunta de múltipla escolha do roteiro fixo.
 *
 * Cada opção já chega com o patch que ela grava, então o toque faz
 * `PATCH /draft` e a conversa anda — sem passar pelo modelo, sem gastar turno
 * e sem o prestador ter que descrever por escrito uma resposta que cabe em
 * três botões.
 *
 * O campo de texto continua embaixo para quem preferir escrever: o cartão é o
 * caminho, não a cerca.
 */
export function ChoiceWidget({
  data,
  editable,
  onAdvance,
}: WidgetProps<ChoiceWidgetData>) {
  const patch = usePatchDraft();
  const [chosen, setChosen] = useState<string | null>(null);

  const answer = async (option: ChoiceOption) => {
    // Trava no primeiro toque, antes de qualquer espera: sair clicando rápido
    // em duas opções gravava as duas e adiantava a conversa dois passos.
    if (chosen) return;
    setChosen(option.id);
    // A desambiguação da atividade grava o ramo (`packSlug`); os demais cartões
    // gravam um patch no payload. Um toque, um dos dois.
    await patch.mutateAsync({
      ...(option.packSlug ? { packSlug: option.packSlug } : {}),
      ...(option.patch ? { payload: option.patch } : {}),
    });
    onAdvance?.(option.label);
  };

  return (
    <WidgetCard title={data.title} icon="help-circle-outline">
        {data.hint ? (
          <ThemedText className="text-xs text-foreground/45">
            {data.hint}
          </ThemedText>
        ) : null}

        <View className="gap-2">
          {(data.options ?? []).map(option => {
            const selected = chosen === option.id;
            return (
              <PressableScale
                key={option.id}
                disabled={!editable || patch.isPending || chosen !== null}
                onPress={() => void answer(option)}
                className={`flex-row items-center gap-3 rounded-xl border px-3 py-3 ${
                  selected
                    ? 'border-primary bg-primary/10'
                    : 'border-border/60 bg-neutral-50'
                }`}
              >
                {option.icon ? (
                  <Ionicons
                    name={option.icon as keyof typeof Ionicons.glyphMap}
                    size={18}
                    color={selected ? PRIMARY : '#9ca3af'}
                  />
                ) : null}
                <View className="flex-1 gap-0.5">
                  <ThemedText className="text-sm font-medium text-foreground">
                    {option.label}
                  </ThemedText>
                  {option.hint ? (
                    <ThemedText className="text-xs text-foreground/45">
                      {option.hint}
                    </ThemedText>
                  ) : null}
                </View>
                {selected ? (
                  <Ionicons name="checkmark-circle" size={18} color={PRIMARY} />
                ) : null}
              </PressableScale>
            );
          })}
        </View>
    </WidgetCard>
  );
}
